import React, { useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../../../contexts/AppContext';
import { toast } from 'react-toastify';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import PurchaseDocumentFormContainer from './PurchaseDocumentFormContainer';
import { PurchaseDocumentServiceManifestDataForm } from '../../../app/models/PurchaseDocument';
import usePurchaseDocumentService from '../../../app/services/hooks/usePurchaseDocumentService';
import { Society } from '../../../app/models/Society';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { Provider } from '../../../app/models/Provider';

interface Props {
    onSaved?: (data: any) => void;
    onCancel?: () => void;
    onError?: () => void;
}

const PurchaseDocumentCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const [purchaseDocument, setPurchaseDocument] = useState<any>({});
    const { navigationPage } = useNavigationPage();
    const [societies, setSocieties] = useState<Society[]>([]);
    const [providers, setProviders] = useState<Provider[]>([]);
    const [paymentStatuses, setPaymentStatuses] = useState<PurchaseDocumentServiceManifestDataForm[]>([]);
    const [errorFields, setErrorFields] = useState<any>();

    const { createPurchaseDocument, fetchingCreatePurchaseDocument } = usePurchaseDocumentService();
    const { storePurchaseDocument, getServiceManifestsByClientSociety, fetchingGetServiceManifestsByClientSociety } = usePurchaseDocumentService();

    useEffect(() => {
        create();
    }, []);

    const create = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');
        createPurchaseDocument({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setProviders(response.data.providers);
                if (hideLoading) hideLoading();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    useEffect(() => {
        if (purchaseDocument.society_id && purchaseDocument.provider_id) {
            getServiceManifestsByClientSociety(purchaseDocument.society_id, purchaseDocument.provider_id, {
                onSuccess: (response: ServiceResponse) => {
                    setPaymentStatuses(response.data.service_manifests);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                }
            });
        }
    }, [purchaseDocument.society_id, purchaseDocument.provider_id]);

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando factura de compra...');
        storePurchaseDocument(purchaseDocument, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.success(response.message, { autoClose: 2500 });
                const purchaseDocumentId = response.data?.tax_document_id;
                if (purchaseDocumentId) {
                    navigationPage(`/tax-documents/${purchaseDocumentId}/edit`);
                }
                if (onSaved) {
                    onSaved(response.data);
                }
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message,
                    message_list: response.data.message_list ?? []
                });
                toast.error(response.message);
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return fetchingCreatePurchaseDocument ? (
        <div>Cargando...</div>
    ) : (
        <PurchaseDocumentFormContainer
            action={store}
            cancel={cancel}
            societies={societies}
            providers={providers}
            purchaseDocument={purchaseDocument}
            setPurchaseDocument={setPurchaseDocument}
            availableStatuses={paymentStatuses}
            errorFields={errorFields}
            updated={false}
            disabled={false}
            loadingServiceManifestStatus={fetchingGetServiceManifestsByClientSociety}
        />
    );
};

export default PurchaseDocumentCreate;